import React from 'react';
import { Table } from '@/components/molecules/Table';
import {
  TableColumn,
  TableAction,
} from '@/components/molecules/Table/Table.props';
import { TableActionsContent } from '@/components/molecules/Table/TableActionsCell';
import { Chip, Icon } from '@/components/atoms';
import { ChipColor } from '@/components/atoms/Chip';
import { Box } from '@mui/material';
import { documentIcons } from '@/assets/icons';
import { DocumentListViewProps, Document } from './DocumentListView.props';

// Helper function to map status color to valid Chip color
const getChipColor = (statusColor: Document['statusColor']): ChipColor => {
  // Map 'error' to 'warning' since Chip doesn't support 'error' color
  return statusColor === 'error' ? 'warning' : (statusColor as ChipColor);
};

export const DocumentListView: React.FC<DocumentListViewProps> = ({
  documents,
  loading = false,
  selectedRows,
  onSelectionChange,
  actions = [],
}) => {
  // Convert actions to TableAction format
  const documentActions: TableAction<Document>[] = actions.map((action) => ({
    id: action.id,
    label: action.label,
    onClick: (doc) => action.onClick(doc),
  }));

  const columns: TableColumn<Document>[] = [
    {
      id: 'fileName',
      label: 'Name',
      width: 250,
      minWidth: 200,
      sortable: true,
      render: (value) => (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Icon
            src={documentIcons.pdf}
            alt="PDF"
            sx={{
              width: 24,
              height: 24,
              fontSize: 24,
            }}
          />
          <Box
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {value as string}
          </Box>
        </Box>
      ),
    },
    {
      id: 'documentType',
      label: 'Document Type',
      width: '15%',
      sortable: true,
    },
    {
      id: 'category',
      label: 'Category',
      width: '15%',
      sortable: true,
    },
    {
      id: 'status',
      label: 'Status',
      width: 176,
      minWidth: 176,
      align: 'left',
      sortable: true,
      render: (value, row) => (
        <Chip
          label={value as string}
          size="s"
          color={getChipColor(row.statusColor)}
          variant="outlined"
        />
      ),
    },
    {
      id: 'description',
      label: 'Description',
      width: '20%',
      sortable: true,
      render: (value) => (
        <Box
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {(value as string) || '—'}
        </Box>
      ),
    },
    {
      id: 'uploadDate',
      label: 'Uploaded',
      width: '10%',
      sortable: true,
    },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      width: 85,
      minWidth: 85,
      sticky: 'right',
      render: (_: unknown, row: Document, index: number) => (
        <TableActionsContent
          row={row}
          index={index}
          actions={documentActions}
        />
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      rows={documents}
      loading={loading}
      hover
      stickyHeader
      selectable
      selectedRows={selectedRows}
      onSelectionChange={onSelectionChange}
      getRowId={(row) => row.id}
      tableHeight="100%"
      tableLayout="auto"
    />
  );
};
